// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** THE LIVE NWS ACTIVE-ALERTS CAPABILITY — real National Weather Service advisories,
 *  published alongside the sealed/ambient tracks, the live ADS-B picture, and the USGS quakes.
 *  An SDK-pure capability (mirrors adsbCapability EXACTLY): it touches NOTHING but HostContext +
 *  the contract types + its OWN data source (nwsSource.ts) — so the dogfood-check SDK-purity
 *  scanner and the no-bypass-check engine-write scanner both stay green.
 *
 *  WHAT IT DOES (the adsbCapability shape exactly):
 *   - On activate it POLLS api.weather.gov active alerts (anonymous REST, NO API KEY, but a
 *     required User-Agent header) every ≥30s and maps each alert feature → a clear-shell
 *     `alert` envelope at the alert geometry's centroid, then PUBLISHES it via ctx.data.publish.
 *     It does NOT paint the map and does NOT write the engine — the HOST runs each envelope
 *     through the PDP and paints the cleared-visible slice (here: all of it, since NWS data is
 *     UNCLASSIFIED). The capability only contributes a layer + publishes (I13: SDK-publish only).
 *   - OFFLINE-GRACEFUL: if NWS is unreachable / returns no features, it falls back to a small
 *     BUNDLED snapshot (nwsSource.NWS_SNAPSHOT_RESPONSE) so the capability still publishes in
 *     dev/offline and tests are deterministic. Network errors are caught + logged, NEVER thrown
 *     into the host. A `mode` opt selects live-vs-snapshot.
 *
 *  CLEAR-SHELL ONLY: public weather alerts are UNCLASSIFIED — payload_mode:"clear",
 *  leakage_profile:"clear", NO sealed/encrypted inner. A natural-hazard advisory is affiliation
 *  "neutral" (never hostile, never friendly). */

import type {
  Capability,
  CapabilityManifest,
  Disposable,
  HostContext,
  LayerSpec,
} from "../../../../packages/contracts-ts/src/index.ts";
import {
  NWS_KIND,
  DEFAULT_POLL_MS,
  MIN_POLL_MS,
  fetchLiveAlerts,
  responseToEnvelopes,
  NWS_SNAPSHOT_RESPONSE,
  type NwsResponse,
} from "./nwsSource.ts";

/** The capability id. */
export const NWS_CAP_ID = "scop:cap:nws";

/** The deck layer this capability owns — a DISTINCT layer id so it never fights another
 *  capability over the registry, though it draws the shared `alert` kind (the host's per-kind
 *  painter routes cleared-visible alert envelopes to it). */
const LAYER_ID = "scop:layer:nws";

/** THE MANIFEST — declared BEFORE activation. NWS contributes ONLY a layer (it has no
 *  commands/panels) and requires the (legacy-permissive, UNCLASSIFIED) subscribe permission.
 *  contributes is EXACTLY [layer] so a mutation that widens it goes red in the check. */
export const NWS_MANIFEST: CapabilityManifest = {
  contributes: ["layer"],
  permissions: [
    {
      subject: {},
      action: "subscribe",
      resource: {
        classification: "UNCLASSIFIED",
        releasability: [],
        mission: [],
        caveats: [],
        originator_control: false,
        policy_ref: "policy://defense.local",
        payload_mode: "clear",
        leakage_profile: "clear",
      },
    },
  ],
};

/** The NWS layer spec — a scatter of weather alerts at their geometry centroids. */
const NWS_LAYER: LayerSpec = {
  id: LAYER_ID,
  kinds: [NWS_KIND],
  type: "ScatterplotLayer",
  props: { radiusMeters: 10_000 },
  updateTriggers: { getFillColor: "severity" },
};

/** Live-vs-snapshot switch. "live" polls NWS (falling back to the snapshot on failure);
 *  "snapshot" publishes the bundled fixture ONCE and never touches the network (dev/offline/
 *  deterministic-test). */
export type NwsMode = "live" | "snapshot";

/** Construction options. */
export interface NwsCapabilityOptions {
  /** Live polling vs the offline snapshot. Default: env COP_NWS_MODE, else "live". */
  mode?: NwsMode;
  /** Poll interval (ms) in live mode; floored at MIN_POLL_MS to respect the feed. */
  pollMs?: number;
  /** Injected fetch (tests). Defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Injected logger (tests / host log routing). Defaults to console. */
  logger?: Pick<Console, "warn" | "info">;
  /** Stable capability id (default NWS_CAP_ID). */
  id?: string;
}

/** Resolve the mode: explicit opt > env (COP_NWS_MODE) > "live". */
function resolveMode(opt?: NwsMode): NwsMode {
  if (opt) return opt;
  const env = typeof process !== "undefined" ? process.env?.COP_NWS_MODE : undefined;
  return env === "snapshot" ? "snapshot" : "live";
}

/** Build the LIVE NWS active-alerts capability. NO host argument, NO PDP — the host's seam
 *  does the clearance filtering (a no-op here, since NWS data is UNCLASSIFIED). PUBLISH-ONLY. */
export function createNwsCapability(opts: NwsCapabilityOptions = {}): Capability {
  const id = opts.id ?? NWS_CAP_ID;
  const mode = resolveMode(opts.mode);
  const pollMs = Math.max(opts.pollMs ?? DEFAULT_POLL_MS, MIN_POLL_MS);
  const fetchImpl = opts.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : undefined);
  const log = opts.logger ?? console;

  let disposables: Disposable[] = [];
  let timer: ReturnType<typeof setInterval> | undefined;
  let abort: AbortController | undefined;
  let disposed = false;

  /** Publish a whole NWS response as alert envelopes through the bus. The HOST runs each
   *  through the PDP and paints the cleared-visible slice; we never paint or write the engine. */
  async function publishResponse(ctx: HostContext, resp: NwsResponse): Promise<void> {
    const envelopes = responseToEnvelopes(resp);
    for (const env of envelopes) {
      if (disposed) return;
      await ctx.data.publish(env);
    }
  }

  /** One LIVE poll: fetch NWS; on ANY failure (network/throttle/malformed) fall back to the
   *  bundled snapshot so the picture never goes blank. Errors are caught in fetchLiveAlerts
   *  (returns null) — this function NEVER throws into the host. */
  async function pollOnce(ctx: HostContext): Promise<void> {
    if (disposed || !fetchImpl) {
      await publishResponse(ctx, NWS_SNAPSHOT_RESPONSE);
      return;
    }
    abort = new AbortController();
    const live = await fetchLiveAlerts(fetchImpl, abort.signal);
    if (disposed) return;
    if (live && (live.features?.length ?? 0) > 0) {
      log.info?.(`[nws] live poll: ${live.features?.length ?? 0} active weather alerts`);
      await publishResponse(ctx, live);
    } else {
      log.warn?.("[nws] feed unreachable/empty — falling back to the bundled snapshot");
      await publishResponse(ctx, NWS_SNAPSHOT_RESPONSE);
    }
  }

  return {
    id,

    async activate(ctx: HostContext): Promise<void> {
      disposed = false;

      // 1. CONTRIBUTE the NWS layer (host-mediated registerLayer). The host creates the GPU
      //    layer and paints the cleared-visible alert envelopes itself — we never touch the
      //    engine's write surface (no-bypass-check stays green).
      disposables = [ctx.registerLayer(NWS_LAYER)];

      // 2. SEED + POLL. Either mode publishes immediately so the map is alive on first paint.
      if (mode === "snapshot") {
        log.info?.("[nws] snapshot mode — publishing the bundled NWS fixture (offline)");
        await publishResponse(ctx, NWS_SNAPSHOT_RESPONSE);
        return;
      }

      // LIVE: one poll now, then on a conservative interval (≥30s).
      await pollOnce(ctx);
      if (disposed) return;
      timer = setInterval(() => {
        void pollOnce(ctx).catch((err) => log.warn?.(`[nws] poll error (ignored): ${String(err)}`));
      }, pollMs);
    },

    deactivate(): void {
      disposed = true;
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
      try {
        abort?.abort();
      } catch {
        // best-effort — aborting an in-flight fetch on teardown
      }
      for (const d of disposables.splice(0)) {
        try {
          d.dispose();
        } catch {
          // teardown is best-effort
        }
      }
    },
  };
}
